import React, { act } from "react";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import _ from "lodash";
import { Drawer } from "@mui/material";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { useSelector } from "react-redux";
import { routes } from "../../../../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import ProviderRequestSearchAndFilters from "./search-and-filters";
import "./provider-requests-list.scss";
import ListingTable from "../../../../../common/components/listing-table/list-table";
import { getProviderRequests } from "../../../../../api/admin/provider-management";
import { headers } from "./provider-requests-helper";
import { PROVIDER_BUSINESS_TYPES } from "../../../../../utils/constants";
import dayjs from "dayjs";
import RequestStatus from "../../../../../common/components/request-status/request-status";
import AdminPaginationComponent from "../../../../../common/components/admin-pagination-component/admin-pagination-component";
import { useSetLoading } from "../../../../../common/hooks/appearance";
// import ViewIcon from "../../../../../common/components/listing-table/view-icon";
import { AlertComponent } from "../../../../../common/components";
import { providerRequestFilterOptions } from "./provider-requests-helper";
import ProviderRequestsFilterOptions from "./provider-requests-filter-options/provider-requests-filter-options";
import "../provider-requests/provider-requests-filter-options/provider-requests-filter-options.scss"

const ProviderRequestLists: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);
  const breadcrumbLinkForList = [
    {
      label: "Provider Management",
      link: routes.PROVIDER_MANAGEMENT,
      handleClick: () => { },
      disablePointerEvents: true,
    },
    { label: "Provider Requests", link: "#", handleClick: () => { } },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || ""
  );
  const [providerRequestsFilterOptions, setProviderRequestsFilterOptions] =
    useState<any[]>(providerRequestFilterOptions);
  const [activeFilters, setActiveFilters] = useState<any>(location?.state?.activeFilters || {});
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void; searchValue: string }>(null);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on search
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  const fetchProviderRequests = async (
    page: number,
    search: string,
    filters: any
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        page,
        limit: pageSize,
        offset,
        search: search,
      };
      // Append filters to params
      if (filters?.status) {
        params.status = filters.status;
      }
      if (filters?.createdTimeStart && filters?.createdTimeEnd) {
        params.fromDate = filters.createdTimeStart.startOf("day").toISOString();
        params.toDate = filters.createdTimeEnd.endOf("day").toISOString();
      }

      if (filters?.businessType && filters.businessType.length) {
        params.businessType = filters.businessType;
      } else {
        params.businessType = PROVIDER_BUSINESS_TYPES;
      }
      const response = await getProviderRequests(params);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data?.data || []);
        setTotalRecords(response?.data?.data?.totalCount || 0);
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch provider requests.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch provider requests."
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchProviderRequests(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => {
        return {
          id: item.id,
          requestId: item.requestId,
          businessName: item.businessName,
          primaryContactName: item.primaryContactName,
          businessType: item.businessType,
          requestedDate: item.requestedDate
            ? dayjs(item.requestedDate).format("MMM D, YYYY")
            : "",
          status: item.status,
          // action: item.action,
        };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      //to clear the state after showing the alert
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.searchText) {
      //from view details page we are sending search text if that already applies in list page
      const searchValue = location.state.searchText;
      setSearchText(searchValue);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && Object.keys(location?.state?.activeFilters || {}).length > 0) {
      const filters = location.state.activeFilters;
      setActiveFilters(filters);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const runFilterActive =
      activeFilters.lastRunStart || activeFilters.lastRunEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);

  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading provider requests...";
    }
    if (searchText && appliedFilterCount === 0) {
      return "No provider requests found for search criteria.";
    }
    if (!searchText && appliedFilterCount > 0) {
      return "No provider requests found matching filter criteria.";
    }
    if (searchText && appliedFilterCount > 0) {
      return "No provider requests found for search and filter criteria.";
    }
    return "No provider requests found";
  }, [loading, searchText, appliedFilterCount]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="provider-requests-main-wrapper">
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <ProfileHeader breadcrumbLink={breadcrumbLink} />
          <div
            className="provider-requests-filter-and-list-wrapper"
            style={{
              display: !isViewOpen ? "flex" : "none",
              flexDirection: "column",
              height: "100%",
              //padding bottom required when we have no data
              paddingBottom: totalRecords === 0 ? "10px" : "0px",
            }}
          >
            <div className="provider-requests-search-and-filters">
              <ProviderRequestSearchAndFilters
                onSearch={handleSearch}
                onFilterChange={setActiveFilters}
                appliedFiltersCount={appliedFilterCount}
                isFilterOpen={isOpenFilter}
                setIsFilterOpen={setIsOpenFilter}
                searchRef={searchRef as any}
                localSearchText={searchText}
                isReset={isResetSearch}
                placeholder="Search by Business Name, Business Type, etc."
              />
            </div>
            <div className="provider-requests-listing-table-wrapper-admin">
              <ListingTable
                headers={headers}
                data={formattedData?.map((item) => ({
                  ...item,
                  status: (
                    <RequestStatus
                      status={
                        item.status
                          ? item.status.charAt(0).toUpperCase() +
                          item.status.slice(1)
                          : ""
                      }
                    />
                  ),
                }))}
                onRowClick={(row) => {
                  navigate(
                    `${routes.PROVIDER_REQUEST_DETAILS.replace(":id", row.id)}`,
                    { state: { searchText: searchText, activeFilters: activeFilters } }
                  );
                }}
                noResultsText={noResultsTxt}
              />
            </div>
            {totalRecords > 0 && (
              <AdminPaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
            <Drawer
              anchor="right"
              open={isOpenFilter}
              onClose={() => setIsOpenFilter(false)}
              sx={{
                zIndex: 1300,
                '& .MuiBackdrop-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  // backdropFilter: 'blur(4px)',
                }
              }}
            >
              <ProviderRequestsFilterOptions
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
                providerRequestsFilterOptions={providerRequestsFilterOptions}
                setProviderRequestsFilterOptions={
                  setProviderRequestsFilterOptions
                }
                activeFilters={activeFilters}
                onApplyFilters={(filters: any) => {
                  setActiveFilters(filters);
                  setCurrentPage(1); // Reset to page 1 on filter apply 

                }}
              />
            </Drawer>
          </div>
        </div>
        <AlertComponent
          open={alertOpen}
          type={alertType}
          message={alertMessage || ""}
          onClose={() => setAlertOpen(false)}
        />
      </div>
    </>
  );
};

export default ProviderRequestLists;
